import React, { useState } from 'react';
import { ShoppingCart, CheckCircle2, AlertTriangle, Info, Tag } from 'lucide-react';
import { FREE_DELIVERY_THRESHOLD, MIN_CART_VALUE } from '../../utils/cartConfig';
import { apiFetch } from '../../utils/api';

const CartSummaryCard = ({ 
  totalQty, 
  subtotal, 
  deliveryCharge, 
  packingCharge, 
  discountAmount = 0,
  grandTotal, 
  sectionsEligibility, 
  isOverallEligible, 
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onCheckout 
}) => {
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // Free delivery progress calculations
  const isFreeDelivery = subtotal >= FREE_DELIVERY_THRESHOLD;
  const remainingForFree = FREE_DELIVERY_THRESHOLD - subtotal;
  const freeDeliveryPercent = Math.min(100, (subtotal / FREE_DELIVERY_THRESHOLD) * 100);

  const handleApplyClick = async (e) => {
    e.preventDefault();
    if (appliedCoupon) {
      setCouponError('Please remove the current coupon before applying a new one.');
      return;
    }

    if (!couponInput || !couponInput.trim()) {
      setCouponError('Coupon code is required.');
      return;
    }

    try {
      setCouponLoading(true);
      setCouponError('');
      const res = await apiFetch('/api/coupons/apply', {
        method: 'POST',
        body: JSON.stringify({ code: couponInput.trim(), subtotal })
      });
      const data = await res.json();

      if (!res.ok) {
        setCouponError(data.message || 'Invalid coupon code.');
        return;
      }

      onApplyCoupon({
        code: data.code,
        discountType: data.discountType,
        discountValue: data.discountValue,
        discountAmount: data.discountAmount
      });
      setCouponInput('');
    } catch (err) {
      console.error(err);
      setCouponError('Failed to apply coupon. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveClick = () => {
    onRemoveCoupon();
    setCouponInput('');
    setCouponError('');
  };

  return (
    <div className="cart-summary-sidebar">
      {/* 1. Header Title */}
      <div className="summary-header">
        <ShoppingCart size={20} className="summary-header-icon" />
        <h3>Cart Summary</h3>
      </div>

      {/* 2. Price Breakdown */}
      <div className="summary-breakdown">
        <div className="summary-row">
          <span className="row-label">Total Items</span>
          <span className="row-value">{totalQty} Pcs</span>
        </div>
        <div className="summary-row">
          <span className="row-label">Subtotal</span>
          <span className="row-value">₹{subtotal.toLocaleString('en-IN')}</span>
        </div>
        <div className="summary-row">
          <span className="row-label">Delivery Charges</span>
          <span className={`row-value ${deliveryCharge === 0 ? 'free-shipping' : ''}`}>
            {deliveryCharge === 0 ? 'Free' : `₹${deliveryCharge.toLocaleString('en-IN')}`}
          </span>
        </div>
        <div className="summary-row">
          <span className="row-label">Packing & Handling Charges</span>
          <span className="row-value">₹{packingCharge.toLocaleString('en-IN')}</span>
        </div>

        {/* 2.5 Apply Coupon Section (Above Grand Total) */}
        <div className="coupon-summary-container">
          {appliedCoupon ? (
            <div className="applied-coupon-card">
              <div className="applied-coupon-info">
                <div className="applied-coupon-title-row">
                  <Tag size={16} className="coupon-tag-icon" />
                  <span className="applied-coupon-code">{appliedCoupon.code}</span>
                  <span className="applied-coupon-percent">
                    ({appliedCoupon.discountType === 'FixedAmount'
                      ? `₹${appliedCoupon.discountValue} OFF`
                      : `${appliedCoupon.discountValue}% OFF`})
                  </span>
                </div>
                <div className="summary-row coupon-discount-detail-row">
                  <span className="row-label">Coupon Amount</span>
                  <span className="row-value coupon-discount-value">- ₹{discountAmount.toLocaleString('en-IN')}</span>
                </div>
              </div>
              <button 
                type="button" 
                onClick={handleRemoveClick}
                className="btn-remove-coupon"
                title="Remove applied coupon"
              >
                Remove
              </button>
            </div>
          ) : (
            <form onSubmit={handleApplyClick} className="coupon-input-wrapper">
              <div className="coupon-input-box">
                <input
                  type="text"
                  className="coupon-text-input"
                  placeholder="Enter Coupon Code"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value);
                    if (couponError) setCouponError('');
                  }}
                />
                <button
                  type="submit"
                  disabled={couponLoading}
                  className="btn-apply-coupon"
                >
                  {couponLoading ? 'Applying...' : 'Apply'}
                </button>
              </div>
            </form>
          )}

          {couponError && (
            <div className="coupon-error-banner">
              <AlertTriangle size={14} />
              <span>{couponError}</span>
            </div>
          )}
        </div>

        <div className="summary-divider" />

        <div className="summary-row grand-total-row">
          <span className="row-label">Grand Total</span>
          <span className="row-value">₹{grandTotal.toLocaleString('en-IN')}</span>
        </div>

        {!isFreeDelivery && (
          <p className="avail-free-text">
            Add ₹{remainingForFree.toLocaleString('en-IN')} more to get FREE delivery
          </p>
        )}
      </div>

      {/* 3. Important Information (Static specs list) */}
      <div className="summary-info-box">
        <div className="info-box-title">
          <Info size={14} className="info-box-icon" />
          <span>Important Information</span>
        </div>
        <ul className="info-rules-list">
          <li><strong>National Articles:</strong> No MOQ requirement.</li>
          <li><strong>Premium & Imported:</strong> MOQ is 30 pcs.</li>
          <li><strong>Accessories:</strong> MOQ is 24 pcs.</li>
          <li><strong>Fragrance & Grooming:</strong> Minimum order value is ₹2000.</li>
        </ul>
      </div>

      {/* 4. Cart Eligibility Checklist */}
      <div className="summary-eligibility-box">
        <h4 className="eligibility-title">Cart Eligibility</h4>
        <div className="eligibility-list">
          {sectionsEligibility.map((sec) => (
            <div key={sec.name} className={`eligibility-item ${sec.eligible ? 'eligible' : 'ineligible'}`}>
              <div className="eligibility-status-icon-wrapper">
                {sec.eligible ? (
                  <CheckCircle2 size={16} className="status-icon" />
                ) : (
                  <AlertTriangle size={16} className="status-icon" />
                )}
              </div>
              <div className="eligibility-item-details">
                <span className="eligibility-item-name">{sec.name}</span>
                <p className="eligibility-item-msg">{sec.message}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 4.5. Cart Value Requirement Checklist */}
      <div className="summary-eligibility-box" style={{ marginTop: '16px', marginBottom: '16px' }}>
        <h4 className="eligibility-title">Cart Value Requirement</h4>
        <div className="eligibility-list">
          <div className={`eligibility-item ${subtotal >= MIN_CART_VALUE ? 'eligible' : 'ineligible'}`}>
            <div className="eligibility-status-icon-wrapper">
              {subtotal >= MIN_CART_VALUE ? (
                <CheckCircle2 size={16} className="status-icon" />
              ) : (
                <AlertTriangle size={16} className="status-icon" />
              )}
            </div>
            <div className="eligibility-item-details">
              <span className="eligibility-item-name">
                {subtotal >= MIN_CART_VALUE 
                  ? "Minimum cart value requirement met." 
                  : "Minimum cart value should be ₹5500 to place the order."}
              </span>
              <p className="eligibility-item-msg">
                {subtotal >= MIN_CART_VALUE 
                  ? "You are eligible to place the order." 
                  : `You need ₹${(MIN_CART_VALUE - subtotal).toLocaleString('en-IN')} more to checkout.`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Free Delivery Progress Bar */}
      <div className="summary-delivery-progress">
        <div className="progress-header">
          <span className="progress-title">FREE DELIVERY PROGRESS</span>
          <span className="progress-values">₹{subtotal.toLocaleString('en-IN')} / ₹{FREE_DELIVERY_THRESHOLD.toLocaleString('en-IN')}</span>
        </div>
        
        <div className="delivery-progress-bar-bg">
          <div 
            className="delivery-progress-bar-fill" 
            style={{ width: `${freeDeliveryPercent}%` }}
          />
        </div>

        <p className="progress-guide-text">
          {isFreeDelivery 
            ? '✓ You have unlocked FREE delivery' 
            : `Add ₹${remainingForFree.toLocaleString('en-IN')} more to get FREE delivery`
          }
        </p>
      </div>

      {/* 6. Checkout Action Button */}
      <button 
        type="button" 
        onClick={onCheckout}
        disabled={!isOverallEligible}
        className="btn-checkout-primary"
      >
        Proceed to Checkout &rarr;
      </button>
    </div>
  );
};

export default CartSummaryCard;
